import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { Spinner } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import { SalarioCheck } from "@/components/CertificadosEmpleado";
import { AfiliacionesConsulta } from "@/components/AfiliacionesConsulta";
import { LlamadosCliente } from "@/components/LlamadosAtencion";
import { ArrowLeft, Award, Download, Eye, Search } from "lucide-react";

const inp = "rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm";

export function ClienteEmpleados() {
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/cliente/empleados", { params: { search: q, estado } }).then((r) => setData(r.data)).catch(() => setData({ empleados: [] })), 300);
    return () => clearTimeout(t);
  }, [q, estado]);
  return (
    <div className="space-y-5" data-testid="cliente-empleados">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mis empleados</h1>
        <p className="mt-1 text-sm text-slate-500">{data?.empresa} · consulta en modo lectura.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><input className={`${inp} w-full pl-9`} placeholder="Nombre, documento, cargo o estado" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cliente-buscar" /></div>
        <select className={inp} value={estado} onChange={(e) => setEstado(e.target.value)} data-testid="cliente-filtro-estado"><option value="">Todos</option><option value="activo">Activos</option><option value="retirado">Retirados</option></select>
        <button onClick={() => downloadFile(`/cliente/empleados/export?estado=${estado}&search=${encodeURIComponent(q)}`, `empleados_${estado || "todos"}.xlsx`)} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800" data-testid="cliente-exportar"><Download className="h-4 w-4" /> Descargar Excel</button>
      </div>
      {!data ? <Spinner className="mx-auto h-6 w-6 text-vino-700" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="border-b text-left text-xs uppercase text-slate-400">{["Empleado", "Documento", "Cargo", "Ingreso", "Terminación", "Estado"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{data.empleados.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cliente-fila-${e.id}`}>
                <td className="px-4 py-3"><Link to={`/empleados/${e.id}`} className="font-medium text-vino-700 hover:underline">{e.nombre_completo}</Link></td>
                <td className="px-4 py-3">{e.cedula}</td><td className="px-4 py-3">{e.cargo}</td><td className="px-4 py-3">{e.fecha_ingreso}</td><td className="px-4 py-3">{e.fecha_terminacion || "—"}</td>
                <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${e.estado === "retirado" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>{e.estado === "retirado" ? "Retirado" : "Activo"}</span></td>
              </tr>
            ))}</tbody>
          </table>
          {data.empleados.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Sin empleados con estos filtros.</p>}
        </div>
      )}
    </div>
  );
}

function CertsCliente({ vid }) {
  const [data, setData] = useState(null);
  const [sal, setSal] = useState({});
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/cliente/empleados/${vid}/certificados`).then((r) => setData(r.data)).catch(() => setData({ contratos: [], certificados: [] }));
  useEffect(() => { load(); }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps
  const emitir = async (k) => {
    setBusy(k.contratacion_id);
    try {
      const { data: rec } = await api.post("/cliente/certificados", { contratacion_id: k.contratacion_id, con_salario: !!sal[k.contratacion_id] });
      toast.success(`Certificado ${rec.consecutivo} generado`);
      await downloadFile(`/cliente/certificados/${rec.id}/download`, `certificado_${rec.consecutivo}.pdf`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (!data) return <Spinner className="h-5 w-5 text-vino-700" />;
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="cliente-certificados">
      <p className="font-semibold text-slate-800">Certificados laborales</p>
      {data.contratos.length === 0 ? <p className="text-sm text-slate-400">Este empleado no tiene información laboral para certificar.</p> : data.contratos.map((k) => (
        <div key={k.contratacion_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3">
          <p className="text-sm">{k.cargo} · {k.numero_contrato || "Datos de Vinculación"}{!k.completo && <span className="block text-xs text-amber-700">Falta: {k.faltantes.join(", ")}</span>}</p>
          <div className="flex items-center gap-2">
            <SalarioCheck k={k} value={!!sal[k.contratacion_id]} onChange={(v) => setSal((p) => ({ ...p, [k.contratacion_id]: v }))} testid={`cliente-cert-salario-${k.contratacion_id}`} />
            <button disabled={!k.completo} onClick={() => setView(k)} className="rounded-lg border border-slate-200 p-2 disabled:opacity-40" data-testid={`cliente-cert-preview-${k.contratacion_id}`}><Eye className="h-4 w-4" /></button>
            <button disabled={!k.completo || !data.firma_disponible || !!busy} onClick={() => emitir(k)} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40" data-testid={`cliente-cert-generar-${k.contratacion_id}`}><Award className="h-4 w-4" /> Generar certificado</button>
          </div>
        </div>
      ))}
      {data.certificados.map((c) => (
        <div key={c.id} className="flex items-center justify-between text-sm" data-testid={`cliente-cert-emitido-${c.id}`}>
          <span>{c.consecutivo} · {c.tipo}</span>
          <button onClick={() => downloadFile(`/cliente/certificados/${c.id}/download`, `certificado_${c.consecutivo}.pdf`)} className="text-vino-700 hover:underline">Descargar</button>
        </div>
      ))}
      {view && <DocViewer url={`/cliente/certificados/preview/${view.contratacion_id}?con_salario=${!!sal[view.contratacion_id]}`} titulo="Vista previa del certificado" subtitulo={view.nombre} nombreArchivo="vista_previa_certificado.pdf" onClose={() => setView(null)} />}
    </div>
  );
}

export function ClienteEmpleado() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/cliente/empleados/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  if (d === null) return <Spinner className="mx-auto h-6 w-6 text-vino-700" />;
  if (!d) return <p className="text-sm text-slate-500">Empleado no encontrado.</p>;
  const campo = (k) => Object.values(d.grupos || {}).flat().find((f) => f.key === k)?.value || "";
  const grupos = [["personales", "Datos personales"], ["contacto", "Contacto"], ["vinculacion", "Vinculación y contrato"], ["seguridad_social", "Seguridad social"]];
  return (
    <div className="space-y-5" data-testid="cliente-empleado-detalle">
      <Link to="/" className="flex items-center gap-1 text-sm text-slate-500 hover:text-vino-700"><ArrowLeft className="h-4 w-4" /> Volver a Mis empleados</Link>
      {grupos.map(([k, t]) => (
        <div key={k} className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="mb-3 font-semibold text-slate-800">{t}</p>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{(d.grupos?.[k] || []).map((f) => <div key={f.key}><dt className="text-xs text-slate-400">{f.label}</dt><dd className="text-sm text-slate-700">{f.value || "—"}</dd></div>)}</dl>
        </div>
      ))}
      <AfiliacionesConsulta base={`/cliente/empleados/${id}/afiliaciones`} rol={`Empresa usuaria ${campo("empresa_cliente")}`.trim()}
        empleado={{ nombre: campo("nombre_completo"), documento: campo("cedula"), empresa: campo("empresa_cliente") }} />
      <CertsCliente vid={id} />
      <LlamadosCliente vid={id} />
    </div>
  );
}
